import React, { useCallback, useRef } from "react";
import { Box, Tab, Tabs } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import BracketByRoundView from "./BracketByRoundView";
import OverviewView from "./OverviewView";
import PlayerJourneyView from "./PlayerJourneyView";
import PoolsListView from "./PoolsListView";
import ResultsSearch, {
  type ResultsSearchSelection,
} from "./ResultsSearch";
import TableauMeta from "./TableauMeta";
import {
  resolveResultsView,
  type BracketDisplayMode,
  type ResultsNavState,
  type ResultsView,
} from "./navigation";
import {
  accentColor,
  isDarkMode,
  surfaceSx,
} from "./resultatsTheme";
import type { PlayerDirectory, ResultsDoc } from "./types";
import styles from "./Resultats.module.css";

export interface TableauNavHandlers {
  setView: (view: ResultsView) => void;
  setPoule: (pouleId: string | null) => void;
  setJoueur: (licence: string | null) => void;
  setTour: (tourId: string | null) => void;
  setMode: (mode: BracketDisplayMode) => void;
  openPlayer: (selection: {
    licence: string;
    tableauId?: string;
    poolId?: string | null;
  }) => void;
}

interface TableauShellProps {
  doc: ResultsDoc;
  boards?: { name: string; label: string }[];
  players: PlayerDirectory;
  searchDocs: ResultsDoc[];
  tournoiLabel?: string | null;
  nav: ResultsNavState;
  handlers: TableauNavHandlers;
}

/** Ordre joueur : poules → tableau final → infos. */
const TAB_VIEWS: Exclude<ResultsView, "player">[] = [
  "pools",
  "bracket",
  "overview",
];

function tabIndex(view: ResultsView): number | false {
  if (view === "player") return false;
  const idx = TAB_VIEWS.indexOf(view);
  return idx >= 0 ? idx : 0;
}

export default function TableauShell({
  doc,
  boards = [],
  players,
  searchDocs,
  tournoiLabel,
  nav,
  handlers,
}: TableauShellProps): React.ReactElement {
  const theme = useTheme();
  const isDark = isDarkMode(theme);
  const searchAnchorRef = useRef<HTMLDivElement>(null);
  const view = resolveResultsView(nav.view, doc);
  const activeTab = tabIndex(view);
  const selected = accentColor(theme);
  const idle = isDark
    ? alpha(theme.palette.common.white, 0.78)
    : theme.palette.text.secondary;

  const onSearchSelect = useCallback(
    (selection: ResultsSearchSelection) => {
      handlers.openPlayer({
        licence: selection.licence,
        tableauId: selection.tableauId,
        poolId: selection.poolId,
      });
    },
    [handlers]
  );

  const focusSearch = useCallback(() => {
    handlers.setView("player");
    window.requestAnimationFrame(() => {
      const input = searchAnchorRef.current?.querySelector("input");
      input?.focus();
    });
  }, [handlers]);

  return (
    <Box
      id={`tableau-${doc.sourceTableauId}`}
      className={styles.tableauBlock}
      component="section"
    >
      <TableauMeta doc={doc} boards={boards} tournoiLabel={tournoiLabel} />

      <Box
        sx={{
          ...surfaceSx(theme),
          p: { xs: 1.5, sm: 2 },
          position: "sticky",
          top: 0,
          zIndex: 8,
          backdropFilter: "blur(10px)",
          bgcolor: alpha(
            theme.palette.background.paper,
            isDark ? 0.92 : 0.94
          ),
        }}
      >
        <Box ref={searchAnchorRef} className={styles.searchSection}>
          <ResultsSearch
            docs={searchDocs}
            boards={boards}
            directory={players}
            onSelect={onSearchSelect}
            selectedLicence={nav.joueur}
          />
        </Box>

        <Tabs
          value={activeTab}
          onChange={(_event, value: number) => {
            const next = TAB_VIEWS[value];
            if (next) handlers.setView(next);
          }}
          variant="fullWidth"
          aria-label="Navigation résultats"
          textColor="inherit"
          TabIndicatorProps={{
            sx: {
              backgroundColor: selected,
              height: 3,
              borderRadius: "3px 3px 0 0",
            },
          }}
          sx={{
            minHeight: 44,
            mt: 0.5,
            "& .MuiTabs-flexContainer": {
              gap: { xs: 0, sm: 0.5 },
            },
            "& .MuiTab-root": {
              minHeight: 44,
              fontWeight: 700,
              fontSize: { xs: "0.9rem", sm: "0.95rem" },
              textTransform: "none",
              color: idle,
              opacity: 1,
              letterSpacing: "-0.01em",
              transition: "color 160ms ease",
              "&.Mui-selected": {
                color: selected,
              },
              "&.Mui-disabled": {
                color: idle,
                opacity: isDark ? 0.35 : 0.4,
              },
              "&:hover": {
                color: isDark
                  ? theme.palette.common.white
                  : theme.palette.text.primary,
              },
            },
          }}
        >
          <Tab label="Poules" disabled={doc.pools.length === 0} />
          <Tab label="Tableau final" disabled={doc.rounds.length === 0} />
          <Tab label="Infos" />
        </Tabs>
      </Box>

      <Box className={styles.viewPane}>
        {view === "pools" && (
          <PoolsListView
            pools={doc.pools}
            players={players}
            selectedPoolId={nav.poule}
            highlightedLicence={nav.joueur}
            onSelectLicence={(licence) =>
              handlers.openPlayer({ licence, poolId: nav.poule })
            }
            onSelectPool={handlers.setPoule}
          />
        )}

        {view === "bracket" && (
          <BracketByRoundView
            rounds={doc.rounds}
            players={players}
            bracketLinkMode={doc.bracketLinkMode}
            selectedTourId={nav.tour}
            mode={nav.mode}
            highlightedLicence={nav.joueur}
            onSelectLicence={(licence) => handlers.openPlayer({ licence })}
            onSelectTour={handlers.setTour}
            onSelectMode={handlers.setMode}
          />
        )}

        {view === "overview" && (
          <OverviewView
            doc={doc}
            onNavigate={handlers.setView}
            onFocusSearch={focusSearch}
          />
        )}

        {view === "player" && (
          <PlayerJourneyView
            doc={doc}
            licence={nav.joueur}
            players={players}
            onBack={() => {
              handlers.setView(doc.pools.length > 0 ? "pools" : "overview");
            }}
            onOpenPool={(poolId) => {
              handlers.setPoule(poolId);
            }}
            onOpenRound={(roundId) => {
              handlers.setTour(roundId);
            }}
            onSelectLicence={(licence) => handlers.openPlayer({ licence })}
          />
        )}
      </Box>
    </Box>
  );
}
